import React, { useContext, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import './index.scss';
import { NonAuthRoutes, Modules, AuthRoutes } from '../../../enums';
import { AuthContext } from '../../../contexts/AuthContext';
import Brand from './Brand';
import LoginIllustrations from './LoginIllustration';
import ForgotPasswordIllustrations from './ForgotPasswordIllustrations';
import ResetPasswordIllustrations from './ResetPasswordIllustrations';

interface PublicLayoutProps {
  route: NonAuthRoutes;
  children: React.ReactNode;
}

const WAVES = [
  { amp: 28, freq: 0.007, phase: 0,   speed: 0.016, color: 'rgba(239,214,59,0.14)', yBase: 0.48 },
  { amp: 20, freq: 0.011, phase: 1.5, speed: 0.022, color: 'rgba(180,120,15,0.11)', yBase: 0.58 },
  { amp: 34, freq: 0.005, phase: 2.8, speed: 0.013, color: 'rgba(239,214,59,0.08)', yBase: 0.65 },
  { amp: 16, freq: 0.015, phase: 0.8, speed: 0.030, color: 'rgba(100,60,10,0.09)',  yBase: 0.40 },
  { amp: 24, freq: 0.009, phase: 3.5, speed: 0.019, color: 'rgba(239,214,59,0.05)', yBase: 0.74 },
  { amp: 14, freq: 0.017, phase: 1.2, speed: 0.027, color: 'rgba(160,100,10,0.07)', yBase: 0.82 },
];

const PublicLayout: React.FC<PublicLayoutProps> = ({ route, children }) => {
  const { token } = useContext(AuthContext);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let t = 0;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      WAVES.forEach(w => {
        ctx.beginPath();
        for (let x = 0; x <= W; x += 4) {
          const y = w.yBase * H + Math.sin(x * w.freq + t * w.speed + w.phase) * w.amp;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.lineTo(W, H);
        ctx.lineTo(0, H);
        ctx.closePath();
        ctx.fillStyle = w.color;
        ctx.fill();
      });

      t++;
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (token) {
    return <Navigate replace to={{ pathname: Modules.home + AuthRoutes.dashboard }} />;
  }

  let illustration;
  if (route === NonAuthRoutes.login) {
    illustration = <LoginIllustrations />;
  } else if (route === NonAuthRoutes.forgotPassword) {
    illustration = <ForgotPasswordIllustrations />;
  } else if (route === NonAuthRoutes.resetPassword) {
    illustration = <ResetPasswordIllustrations />;
  }

  return (
    <main className="login-main">
      {/* Full-screen wave canvas behind everything */}
      <canvas ref={canvasRef} className="login-fullscreen-canvas" />

      <div className="authentication-wrapper authentication-cover">
        <Brand />
        <div className="authentication-inner row m-0">
          <div className="d-none d-lg-flex col-lg-6 col-xl-6 align-items-center p-0">
            {illustration}
          </div>
          <div className="d-flex col-12 col-lg-6 col-xl-6 align-items-center authentication-bg position-relative py-sm-12 px-12 py-6">
            <div className="login-form-card">
              {children}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PublicLayout;
